// src/cockpit4/workspaces/MarketingWorkspace.tsx
// ============================================
// Marketing Workspace - 复用 MarketingDashboard 组件
//
// 包装在玻璃拟态容器中，统一 Cockpit 4.0 视觉风格
// 底部集成 ContentBrowserPanel（可折叠）浏览内容产物
// 集成"内容工厂产物"区块，可在营销中心直接查看 MediaStudio 产物
// ============================================

import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Megaphone,
  ChevronDown,
  FolderOpen,
  Sparkles,
  RefreshCw,
  Loader2,
  ImageIcon,
  Video,
  Music,
  FileText,
  ExternalLink,
  Calendar,
  Stethoscope,
  Flame,
  Newspaper,
  Filter,
  Users,
  Eye,
  Layers,
  // P2 标签图标
  Target,
  MessageCircle,
  PieChart,
} from "lucide-react";
import MarketingDashboard, { type MarketingPlatform } from "../../components/Dashboard/MarketingDashboard";
// v6.3.22 品牌常量（唯一真相源：与内容工厂共用，保证两边品牌一致）
import { BRAND_OPTIONS } from "../../services/brands";
import { useDashboardData } from "../../hooks/useDashboardData";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useCockpit4Store } from "../store";
// Phase 3 P0-3：页面顶部业务说明 + 跨模块跳转
import WorkspaceBizBar from "../components/WorkspaceBizBar";
import type { MediaPackage } from "../../runtime/MediaStudio/types";

// 懒加载内容浏览面板（避免静态依赖阻断首屏）
const ContentBrowserPanel = lazy(() => import("./ContentBrowserPanel"));

// 抖音直播策略面板（独立子模块，仅抖音平台显示）
import DouyinLiveStrategyPanel from "../components/DouyinLiveStrategyPanel";
// P0-功能1：智能营销日历
import MarketingCalendar from "../components/MarketingCalendar";
// P0-功能2：营销效果诊断与优化建议
import MarketingDiagnosis from "../components/MarketingDiagnosis";
// v5.2 多平台内容对比实验室（流程链路版 vs 凭空瞎写版，6 大平台持续改进）
import ContentCompareLab from "../components/ContentCompareLab";
// P0-功能3：热点追踪与快速响应
import HotspotTracker from "../components/HotspotTracker";
import { DailyDynamicsPanel } from "./HermesWorkspace";
// P1 新增：营销中心四个子组件（漏斗/用户画像/竞品/跨平台策略）
import MarketingFunnel from "../components/MarketingFunnel";
import UserPersona from "../components/UserPersona";
import CompetitorMonitor from "../components/CompetitorMonitor";
import CrossPlatformStrategy from "../components/CrossPlatformStrategy";
// P2-功能8：智能投放助手
import SmartAdAssistant from "../components/SmartAdAssistant";
// P2-功能9：私域运营自动化
import PrivateDomainAutomation from "../components/PrivateDomainAutomation";
// P2-功能10：营销 ROI 仪表盘
import MarketingROIDashboard from "../components/MarketingROIDashboard";

// ============================================
// 内容工厂产物区块
// ============================================

/** v5.2 兜底：直接从 Vault 恢复媒体包（不依赖 MediaStudio 内存态） */
async function fetchPackagesFromVault(): Promise<MediaPackage[]> {
  const packages: MediaPackage[] = [];
  try {
    const rootResp = await fetch(`/vault-api/list?dir=${encodeURIComponent('XEAOS/ContentPackages')}&ext=.md`);
    if (!rootResp.ok) return packages;
    const rootResult = await rootResp.json();
    if (!rootResult.success) return packages;
    const dateDirs = (rootResult.files || []).filter((f: any) => f.isDirectory);
    await Promise.all(dateDirs.map(async (dir: any) => {
      try {
        const subResp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir.path)}&ext=.md`);
        if (!subResp.ok) return;
        const subResult = await subResp.json();
        if (!subResult.success) return;
        for (const file of (subResult.files || [])) {
          const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(file.path)}`);
          if (!readResp.ok) continue;
          const readResult = await readResp.json();
          if (!readResult.success || !readResult.content) continue;
          const content = String(readResult.content);
          const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
          if (!fmMatch) continue;
          const fm = fmMatch[1];
          const get = (key: string): string => {
            const m = fm.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
            return m ? m[1].trim().replace(/^["']|["']$/g, '') : '';
          };
          const id = get('id');
          if (!id) continue;
          const pkg: MediaPackage = {
            id,
            topic: get('topic'),
            status: (get('status') as MediaPackage['status']) || 'completed',
            createdAt: get('createdAt') || new Date().toISOString(),
            completedAt: get('completedAt') || undefined,
            description: get('description') || undefined,
            sourceNotes: [],
            _vaultPath: file.path,
          } as MediaPackage;
          const scriptRegex = /<!-- XEAOS_SCRIPT:(\w+) -->\n([\s\S]*?)(?=<!-- XEAOS_SCRIPT:|## 素材链接|$)/g;
          let sm: RegExpExecArray | null;
          const scriptIndex: Record<string, string> = {};
          while ((sm = scriptRegex.exec(content)) !== null) {
            scriptIndex[sm[1]] = sm[2].replace(/\n+$/, '').trim();
          }
          if (scriptIndex.article_script) {
            pkg.articleScript = { id: `asset-${id}-article`, type: 'script' as const, purpose: 'article_script' as const, content: scriptIndex.article_script, title: `公众号文章脚本：${pkg.topic}`, model: 'deepseek-chat', status: 'completed' as const, createdAt: pkg.createdAt };
          }
          if (scriptIndex.douyin_script) {
            pkg.douyinScript = { id: `asset-${id}-douyin`, type: 'script' as const, purpose: 'douyin_script' as const, content: scriptIndex.douyin_script, title: `抖音完整脚本：${pkg.topic}`, model: 'deepseek-chat', status: 'completed' as const, createdAt: pkg.createdAt };
          }
          if (scriptIndex.xiaohongshu_script) {
            pkg.xiaohongshuScript = { id: `asset-${id}-xhs`, type: 'script' as const, purpose: 'xiaohongshu_script' as const, content: scriptIndex.xiaohongshu_script, title: `小红书图文脚本：${pkg.topic}`, model: 'deepseek-chat', status: 'completed' as const, createdAt: pkg.createdAt };
          }
          if (scriptIndex.bilibili_script) {
            pkg.bilibiliScript = { id: `asset-${id}-bili`, type: 'script' as const, purpose: 'bilibili_script' as const, content: scriptIndex.bilibili_script, title: `B站视频脚本：${pkg.topic}`, model: 'deepseek-chat', status: 'completed' as const, createdAt: pkg.createdAt };
          }
          if (scriptIndex.video_script) {
            pkg.videoScript = { id: `asset-${id}-video`, type: 'script' as const, purpose: 'video_script' as const, content: scriptIndex.video_script, title: `视频分镜脚本：${pkg.topic}`, model: 'deepseek-chat', status: 'completed' as const, createdAt: pkg.createdAt };
          }
          packages.push(pkg);
        }
      } catch {
        // 忽略单个目录失败
      }
    }));
  } catch {
    // 忽略整体失败
  }
  return packages;
}

interface MediaStudioProductsProps {
  /** 当前激活的平台，用于过滤产物 */
  activePlatform: MarketingPlatform;
}

function MediaStudioProducts({ activePlatform }: MediaStudioProductsProps) {
  const [packages, setPackages] = useState<MediaPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPkg, setSelectedPkg] = useState<MediaPackage | null>(null);
  const [articlePreview, setArticlePreview] = useState<{ title: string; content: string; loading: boolean } | null>(null);
  // v5.2：内容类型分类筛选（默认"全部"；按产物实际含有的内容类型归类）
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const CATEGORIES = [
    { id: "all", label: "全部" },
    { id: "article", label: "公众号文章" },
    { id: "douyin", label: "抖音脚本" },
    { id: "xiaohongshu", label: "小红书图文" },
    { id: "bilibili", label: "B站视频" },
    { id: "video", label: "视频分镜" },
    { id: "live", label: "直播脚本" },
    { id: "cover", label: "封面图" },
  ];
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  // v5.2：监听工作区切换，进入营销中心时自动刷新产物（生成后无需手动刷新）
  const currentWorkspace = useCockpit4Store((s) => s.currentWorkspace);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 等待 RuntimeManager 就绪
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        return;
      }
      let list: MediaPackage[] = [];
      try {
        const resp = await RuntimeManager.query(RuntimeName.MediaStudio, {
          type: "list_packages",
        });
        if (resp?.success) {
          const data = resp.data as any;
          list = data?.packages || (Array.isArray(data) ? data : []) || [];
        }
      } catch {
        // 忽略 Runtime 查询失败，走 Vault 兜底
      }
      // v5.2 兜底升级：无论 Runtime 返回是否为空，都合并 Vault 恢复结果（按 id 去重）
      // 根因：list_packages 可能只返回 MediaStudio 内存旧包，对比生成等前端直写 Vault 的新包会丢失
      try {
        const vaultPkgs = await fetchPackagesFromVault();
        if (vaultPkgs.length > 0) {
          const byId = new Map<string, MediaPackage>();
          for (const p of vaultPkgs) byId.set(p.id, p);
          for (const p of list) byId.set(p.id, p); // Runtime 包优先（含完整素材）
          list = Array.from(byId.values());
        }
      } catch {
        // 忽略
      }
      setPackages(list);
      if (list.length > 0 && !selectedPkg) {
        setSelectedPkg(list[0]);
      }
      if (list.length === 0 && error === null) {
        // 无产物时不视为错误，仅提示空态
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  // v5.2：每次切回营销中心时自动刷新内容工厂产物（避免"生成后看不到"）
  useEffect(() => {
    if (currentWorkspace === "marketing") {
      loadPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace]);

  // 判断包的内容类型（用于分类标签与筛选）
  const pkgCategories = (pkg: MediaPackage): string[] => {
    const cats: string[] = [];
    if (pkg.articleScript) cats.push("article");
    if (pkg.douyinScript) cats.push("douyin");
    if (pkg.xiaohongshuScript || pkg.xiaohongshuGrid) cats.push("xiaohongshu");
    if (pkg.bilibiliScript) cats.push("bilibili");
    if (pkg.videoScript) cats.push("video");
    if (pkg.liveStreamScript) cats.push("live");
    if (pkg.cover || pkg.wechatCover || pkg.douyinCover) cats.push("cover");
    return cats.length > 0 ? cats : ["other"];
  };

  // 按当前平台过滤媒体包
  const filteredPackages = useMemo(() => {
    let list = packages.filter((pkg) => {
      switch (activePlatform) {
        case "wechat_official":
          // 公众号：文章脚本或公众号配图
          return !!(pkg.articleScript || pkg.wechatCover);
        case "xiaohongshu":
          // 小红书：九宫格或小红书脚本
          return !!(pkg.xiaohongshuGrid || pkg.xiaohongshuScript);
        case "douyin_enterprise":
        case "douyin_personal":
          // 抖音：抖音封面或抖音脚本
          return !!(pkg.douyinCover || pkg.douyinScript);
        case "bilibili":
          // B站：B站脚本
          return !!pkg.bilibiliScript;
        case "zhihu":
          // 知乎：暂复用公众号文章脚本
          return !!pkg.articleScript;
        default:
          return false;
      }
    });
    // v5.2 内容类型分类筛选（非"全部"时按类型过滤）
    if (categoryFilter !== "all") {
      list = list.filter((pkg) => pkgCategories(pkg).includes(categoryFilter));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages, activePlatform, categoryFilter]);

  // v5.2 看板联动：产物统计（供 MarketingDashboard 使用）
  const productStats = useMemo(() => {
    const total = packages.length;
    const published = packages.filter((p) => p.published === true).length;
    const withScript = packages.filter((p) => p.articleScript || p.douyinScript || p.xiaohongshuScript || p.bilibiliScript || p.videoScript).length;
    const withCover = packages.filter((p) => p.cover || p.wechatCover || p.douyinCover).length;
    return { total, published, withScript, withCover };
  }, [packages]);

  return (
    <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.02] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles size={12} className="text-purple-400" />
          <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider">
            内容工厂产物
          </span>
          <span className="text-[10px] font-mono text-gray-600">
            最近生成的媒体包 · {productStats.total} 个 · 脚本 {productStats.withScript} · 封面 {productStats.withCover}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWorkspace("media_studio")}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded transition-colors"
            title="跳转到内容工厂"
          >
            <ExternalLink size={10} />
            打开工作室
          </button>
          <button
            onClick={loadPackages}
            disabled={loading}
            className="p-1 text-gray-500 hover:text-purple-400 transition-colors"
            title="刷新"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* v5.2 内容类型分类筛选：按产物类型查看，不再一锅乱炖 */}
      <div className="flex flex-wrap gap-1 mb-2">
        {CATEGORIES.map((cat) => {
          const active = categoryFilter === cat.id;
          const count = cat.id === "all"
            ? packages.length
            : packages.filter((p) => pkgCategories(p).includes(cat.id)).length;
          return (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                active
                  ? "bg-purple-500/15 border-purple-500/40 text-purple-200"
                  : "bg-white/[0.02] border-white/[0.06] text-gray-500 hover:border-purple-500/30"
              }`}
            >
              {cat.label} <span className="opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-2 text-[10px] font-mono text-red-400 px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 size={14} className="animate-spin text-purple-400" />
          <span className="ml-2 text-[11px] font-mono text-gray-500">加载媒体包...</span>
        </div>
      ) : filteredPackages.length === 0 ? (
        <p className="text-[11px] text-gray-600 text-center py-4">
          当前平台暂无媒体包，请前往内容工厂生成
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {/* 左侧：媒体包列表 */}
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {filteredPackages.map((pkg) => {
              const active = selectedPkg?.id === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPkg(pkg)}
                  className={`w-full text-left px-2 py-1.5 rounded border transition-colors ${
                    active
                      ? "bg-purple-500/10 border-purple-500/30"
                      : "bg-white/[0.01] border-white/[0.04] hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 mb-0.5">
                    <span className="text-[11px] text-gray-200 font-medium truncate flex-1">
                      {pkg.topic}
                    </span>
                    <span
                      className={`text-[9px] font-mono px-1 py-0.5 rounded flex-shrink-0 ${
                        pkg.status === "completed"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : pkg.status === "failed"
                          ? "bg-red-500/10 text-red-400"
                          : "bg-yellow-500/10 text-yellow-400"
                      }`}
                    >
                      {pkg.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] font-mono text-gray-600">
                    {pkg.createdAt && (
                      <span>{pkg.createdAt.slice(0, 10)}</span>
                    )}
                    {/* v5.2 内容类型标签：一眼看出是什么内容 */}
                    {pkgCategories(pkg).map((cat) => {
                      const meta: Record<string, { label: string; color: string }> = {
                        article: { label: "公众号", color: "#10B981" },
                        douyin: { label: "抖音", color: "#000000" },
                        xiaohongshu: { label: "小红书", color: "#FF2442" },
                        bilibili: { label: "B站", color: "#FB7299" },
                        video: { label: "分镜", color: "#06B6D4" },
                        live: { label: "直播", color: "#FF6B6B" },
                        cover: { label: "封面", color: "#EC4899" },
                        other: { label: "素材", color: "#8B5CF6" },
                      };
                      return (
                        <span
                          key={cat}
                          className="px-1 py-0.5 rounded text-[8px] font-mono"
                          style={{ background: `${meta[cat]?.color || '#888'}20`, color: meta[cat]?.color || '#888' }}
                        >
                          {meta[cat]?.label || cat}
                        </span>
                      );
                    })}
                    {pkg.cover && <ImageIcon size={9} className="text-pink-400" />}
                    {pkg.articleScript && <FileText size={9} className="text-blue-400" />}
                    {pkg.video && <Video size={9} className="text-cyan-400" />}
                    {pkg.bgm && <Music size={9} className="text-purple-400" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* 右侧：选中媒体包详情 */}
          <div className="rounded border border-white/[0.04] bg-white/[0.01] p-2 max-h-[280px] overflow-y-auto">
            {selectedPkg ? (
              <div className="space-y-1.5">
                <div className="text-[12px] text-gray-200 font-medium">
                  {selectedPkg.topic}
                </div>
                {selectedPkg.description && (
                  <div className="text-[10px] text-gray-500 line-clamp-2">
                    {selectedPkg.description}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
                  <div className="px-1.5 py-1 rounded bg-white/[0.03]">
                    <span className="text-gray-500">封面：</span>
                    <span className={selectedPkg.cover ? "text-emerald-400" : "text-gray-600"}>
                      {selectedPkg.cover ? "✓" : "—"}
                    </span>
                  </div>
                  <div className="px-1.5 py-1 rounded bg-white/[0.03]">
                    <span className="text-gray-500">公众号脚本：</span>
                    <span className={selectedPkg.articleScript ? "text-emerald-400" : "text-gray-600"}>
                      {selectedPkg.articleScript ? `${selectedPkg.articleScript.content?.length || 0} 字` : "—"}
                    </span>
                  </div>
                  <div className="px-1.5 py-1 rounded bg-white/[0.03]">
                    <span className="text-gray-500">视频：</span>
                    <span className={selectedPkg.video ? "text-emerald-400" : "text-gray-600"}>
                      {selectedPkg.video ? "✓" : "—"}
                    </span>
                  </div>
                  <div className="px-1.5 py-1 rounded bg-white/[0.03]">
                    <span className="text-gray-500">BGM：</span>
                    <span className={selectedPkg.bgm ? "text-emerald-400" : "text-gray-600"}>
                      {selectedPkg.bgm ? "✓" : "—"}
                    </span>
                  </div>
                </div>
                {selectedPkg.sourceNotes && selectedPkg.sourceNotes.length > 0 && (
                  <div className="text-[9px] font-mono text-cyan-600">
                    📚 关联 {selectedPkg.sourceNotes.length} 篇知识库笔记
                  </div>
                )}
                {/* 预览公众号文章脚本（关联营销中心的公众号内容） */}
                {selectedPkg.articleScript && (
                  <button
                    onClick={() => setArticlePreview({
                      title: selectedPkg.topic,
                      content: selectedPkg.articleScript?.content || '',
                      loading: false,
                    })}
                    className="w-full mt-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded transition-colors"
                    title="预览公众号文章内容"
                  >
                    <FileText size={10} />
                    预览公众号文章
                  </button>
                )}
                <button
                  onClick={() => setWorkspace("media_studio")}
                  className="w-full mt-1 flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 rounded transition-colors"
                >
                  <ExternalLink size={10} />
                  在内容工厂查看
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-gray-600 text-center py-4">
                选择左侧媒体包查看详情
              </p>
            )}
          </div>
        </div>
      )}

      {/* 公众号文章预览 Modal（关联营销中心公众号内容） */}
      <AnimatePresence>
        {articlePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setArticlePreview(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-blue-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-blue-400" />
                  <h3 className="text-[14px] font-semibold text-white">{articlePreview.title}</h3>
                  <span className="text-[10px] font-mono text-gray-600 ml-2 px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                    公众号文章预览
                  </span>
                </div>
                <button
                  onClick={() => setArticlePreview(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                <pre className="text-[13px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {articlePreview.content || '暂无文章内容'}
                </pre>
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[11px] font-mono text-gray-600">
                  数据来源：内容工厂 → 营销中心（关联公众号内容）
                </span>
                <button
                  onClick={() => setWorkspace("media_studio")}
                  className="text-[11px] font-mono text-blue-300 hover:text-blue-200"
                >
                  前往内容工厂编辑 →
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MarketingWorkspace() {
  const dashboard = useDashboardData();
  // 内容浏览面板默认折叠，避免占据营销指标空间
  const [contentPanelOpen, setContentPanelOpen] = useState(false);
  // 提升平台 Tab 状态，供 Dashboard 与内容工厂产物共享
  const [activePlatform, setActivePlatform] = useState<MarketingPlatform>("wechat_official");
  // P0/P1 新增：标签页切换（P0: 数据看板/营销日历/效果诊断/热点追踪 + P1: 营销漏斗/用户画像/竞品监控/跨平台策略）
  const [activeSection, setActiveSection] = useState<
    | "dashboard"
    | "calendar"
    | "diagnosis"
    | "hotspot"
    | "dynamic"
    | "funnel"
    | "persona"
    | "competitor"
    | "crossStrategy"
    | "smartAd"
    | "privateDomain"
    | "roi"
  >("dashboard");
  // v6 收敛：低频功能收进"更多▾"下拉
  const [showMoreSections, setShowMoreSections] = useState(false);

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
              <Megaphone size={14} className="text-pink-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                营销中心
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                营销分析 · 多平台 · AI 增长引擎
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            公众号 · 小红书 · 抖音 · 知乎
          </span>
        </div>

        {/* Phase3 P0-3 业务说明 + 跨模块跳转 */}
        <WorkspaceBizBar
          desc="营销中心：监控多平台内容流量，将公域流量转化为客户线索"
          hint="产出的线索自动同步到【客户中心】"
          accent="#EC4899"
          jumps={[
            { label: "生成内容", to: "media_studio" },
            { label: "查看新增线索", to: "crm" },
          ]}
        />

        {/* v6 收敛：核心 5 项平铺（含私域运营） + 低频 6 项收进"更多▾" */}
        <div className="relative z-30 flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] flex-wrap">
          {/* 核心标签组 */}
          <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider mr-1">
            核心
          </span>
          {(
            [
              { id: "dashboard", label: "数据看板", icon: Megaphone },
              { id: "calendar", label: "营销日历", icon: Calendar },
              { id: "diagnosis", label: "效果诊断", icon: Stethoscope },
              { id: "hotspot", label: "热点追踪", icon: Flame },
              { id: "dynamic", label: "动态洞察", icon: Newspaper },
              { id: "privateDomain", label: "私域运营", icon: MessageCircle },
            ] as const
          ).map((tab) => {
            const TIcon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                  isActive
                    ? "bg-pink-500/15 text-pink-300 border border-pink-500/30"
                    : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <TIcon size={11} />
                {tab.label}
              </button>
            );
          })}

          {/* 更多▾ 下拉 */}
          <div className="relative">
            <button
              onClick={() => setShowMoreSections(!showMoreSections)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                showMoreSections
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>更多</span>
              <ChevronDown size={10} className={showMoreSections ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {showMoreSections && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMoreSections(false)} />
                <div className="absolute top-full left-0 mt-1 z-40 min-w-[160px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl">
                  {(
                    [
                      { id: "funnel", label: "营销漏斗", icon: Filter },
                      { id: "persona", label: "用户画像", icon: Users },
                      { id: "competitor", label: "竞品监控", icon: Eye },
                      { id: "crossStrategy", label: "跨平台策略", icon: Layers },
                      { id: "smartAd", label: "智能投放", icon: Target },
                      { id: "roi", label: "营销ROI", icon: PieChart },
                    ] as const
                  ).map((tab) => {
                    const TIcon = tab.icon;
                    const isActive = activeSection === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveSection(tab.id);
                          setShowMoreSections(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                          isActive
                            ? "text-pink-300 bg-pink-500/10"
                            : "text-gray-300 hover:bg-white/[0.05]"
                        }`}
                      >
                        <TIcon size={11} className="text-pink-400" />
                        <span className="flex-1 text-left">{tab.label}</span>
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-pink-400" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Tab 0：数据看板（原有功能） */}
          {activeSection === "dashboard" && (
            <>
              {/* v6.3.22 品牌状态条：与内容工厂共用同一份品牌常量（唯一真相源），两边品牌一致 */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.04] bg-white/[0.01]">
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mr-1">品牌</span>
                {BRAND_OPTIONS.map((b) => (
                  <span
                    key={b.id}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono"
                    style={{ background: `${b.color}1a`, color: b.color }}
                    title={b.desc + (b.public ? "（对外公开）" : "（量化类，不上公开平台）")}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: b.color, opacity: b.public ? 1 : 0.4 }}
                    />
                    {b.name}
                    {!b.public && <span className="text-gray-500">·内部</span>}
                  </span>
                ))}
              </div>

              <MarketingDashboard
                data={dashboard.marketing}
                active={activePlatform}
                onActiveChange={setActivePlatform}
              />

              {/* ============ 内容工厂产物区块 ============ */}
              <MediaStudioProducts activePlatform={activePlatform} />

              {/* ============ 抖音直播策略子模块（仅抖音平台显示） ============ */}
              <AnimatePresence initial={false}>
                {(activePlatform === "douyin_enterprise" ||
                  activePlatform === "douyin_personal") && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <DouyinLiveStrategyPanel />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ============ 内容产物浏览面板（可折叠） ============ */}
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] overflow-hidden">
                {/* 折叠标题栏 */}
                <button
                  onClick={() => setContentPanelOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen size={12} className="text-cyan-400" />
                    <span className="text-[11px] font-mono uppercase tracking-wider text-gray-300">
                      内容产物浏览
                    </span>
                    <span className="text-[9px] font-mono text-gray-600">
                      ContentPackage · Douyin-Scripts · Review-Queue · Publish-Log
                    </span>
                  </div>
                  <ChevronDown
                    size={12}
                    className={`text-gray-500 transition-transform ${
                      contentPanelOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* 展开内容 */}
                <AnimatePresence initial={false}>
                  {contentPanelOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="border-t border-white/[0.06] overflow-hidden"
                    >
                      <div className="h-[420px]">
                        <Suspense
                          fallback={
                            <div className="h-full flex items-center justify-center text-[11px] font-mono text-gray-500">
                              加载内容浏览器...
                            </div>
                          }
                        >
                          <ContentBrowserPanel />
                        </Suspense>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}

          {/* Tab 1：智能营销日历（P0-功能1） */}
          {activeSection === "calendar" && <MarketingCalendar />}

          {/* Tab 2：营销效果诊断（P0-功能2） */}
          {activeSection === "diagnosis" && (
            <div className="space-y-3">
              <MarketingDiagnosis />
              {/* v5.2 多平台内容对比实验室：流程链路版 vs 凭空瞎写版（6 大平台持续改进） */}
              <ContentCompareLab />
            </div>
          )}

          {/* Tab 3：热点追踪（P0-功能3） */}
          {activeSection === "hotspot" && <HotspotTracker />}
          {activeSection === "dynamic" && <DailyDynamicsPanel />}

          {/* P1-功能4：营销漏斗分析 */}
          {activeSection === "funnel" && <MarketingFunnel />}

          {/* P1-功能5：用户画像与智能分群 */}
          {activeSection === "persona" && <UserPersona />}

          {/* P1-功能6：竞品监控与分析 */}
          {activeSection === "competitor" && <CompetitorMonitor />}

          {/* P1-功能7：跨平台内容策略引擎 */}
          {activeSection === "crossStrategy" && <CrossPlatformStrategy />}

          {/* P2-功能8：智能投放助手 */}
          {activeSection === "smartAd" && <SmartAdAssistant />}

          {/* P2-功能9：私域运营自动化 */}
          {activeSection === "privateDomain" && <PrivateDomainAutomation />}

          {/* P2-功能10：营销 ROI 仪表盘 */}
          {activeSection === "roi" && <MarketingROIDashboard />}
        </div>
      </motion.div>
    </div>
  );
}
